import { get, off, onValue, ref, update } from "firebase/database";
import { db } from "../lib/firebase";
import { getPathDatabaseByRole } from "./auth";
import { getUserID } from "./getters";
import { CREATIVE_JOB_TYPES, LaunchError } from "../utils/errors";
import { getKeyDatabase, startUploadFileAws } from "../services/public";
import {
  ALERTS_ASYNC_STATUS,
  CREATIVE_TEMPLATE_ANALYSIS_STATUS,
  CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID,
  CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_SIZE,
  CREATIVE_TEMPLATE_MAX_NODES,
  CREATIVE_TEMPLATE_PRODUCE_STATUS,
  CREATIVE_TEMPLATE_SCHEMA_VERSION,
  CREATIVE_TEMPLATE_STATUS_DRAFT,
} from "../utils/constants";
import { parseCreativeTemplateDoc } from "../features/creativeTemplates/schemas/creativeTemplateDocSchema";
import {
  countNodes,
  sanitizeArtboardsForPersist,
  sanitizeNodesForPersist,
} from "../features/creativeTemplates/utils/layerTree";
import {
  countAllArtboardNodes,
  findReferenceArtboardID,
  getCardArtboardFields,
  getReferenceAssetKey,
  hydrateArtboards,
  listBackdropAssets,
  resolveActiveArtboardID,
  resolveArtboardSizeFromDimensions,
} from "../features/creativeTemplates/utils/artboards";
import { canWriteCreativeTemplates } from "../features/creativeTemplates/utils/permissions";
import { getImageDimensionsFromFile } from "../features/creativeTemplates/utils/getImageDimensionsFromFile";
import { addNotification, updateNotification } from "./alerts";
import { createCreativeJobAndWaitForResponse } from "./jobs";
import { promoteCreativeJobNotification } from "./creativeJobs";
import { getUniqueID } from "../utils/numbers";
import { CREATIVE_JOB_STATUS } from "../utils/creativeJobStatus";
import {
  getCreativeJobAlertMessageKey,
  mapJobResultToGenerationData,
} from "../utils/creativeJobsNotifications";
import {
  startSaveCreativeTemplateProduce,
  startUpdateCreativeTemplateProduceStatus,
} from "./creativeTemplatesProduce";

const getCardsPath = ({ advertiserID }) =>
  `creativeTemplatesCards/${advertiserID}`;

const getTemplatesPath = ({ advertiserID }) =>
  `creativeTemplates/${advertiserID}`;

const getTemplatePath = ({ advertiserID, templateID }) =>
  `${getTemplatesPath({ advertiserID })}/${templateID}`;

const getTemplateDocPath = ({ advertiserID, templateID }) =>
  `${getTemplatePath({ advertiserID, templateID })}/doc`;

const getAssetPath = ({ advertiserID, assetID }) =>
  `creativeAssets/${advertiserID}/${assetID}`;

const getUploadPath = ({ advertiserID, templateID }) =>
  `creative-template-assets/${advertiserID}/${templateID}/`;

const toIso = ({ timestamp }) => new Date(timestamp).toISOString();

const buildTimeFields = ({ creationTime, updateTime }) => ({
  creationTime,
  creationTimeNegative: creationTime * -1,
  updateTime,
  updateTimeNegative: updateTime * -1,
});

const resolveCreationTime = ({ card, fallback }) =>
  card?.creationTime || card?.createdAt || fallback;

const buildAudit = ({
  existingAudit = {},
  createdBy,
  creationTime,
  updateTime,
}) => ({
  createdBy: createdBy || existingAudit.createdBy,
  creationTime:
    creationTime != null
      ? toIso({ timestamp: creationTime })
      : existingAudit.creationTime || existingAudit.createdAt,
  updateTime: toIso({ timestamp: updateTime }),
});

const parseTemplateDoc = ({
  doc,
  dispatch,
  functionName,
  templateID,
}) => {
  const parsed = parseCreativeTemplateDoc(doc);
  if (parsed.success) return parsed;
  console.error(`${functionName} invalidGraph`, parsed.error.issues);
  dispatch(
    LaunchError(parsed.error, false, [], {
      functionName,
      context: { templateID, issues: parsed.error.issues },
      tags: { action: "creativeTemplates", reason: "invalidGraph" },
      level: "error",
    })
  );
  return parsed;
};

const buildCard = ({
  templateID,
  advertiserID,
  name,
  status,
  thumbnailUrl,
  width,
  height,
  nodeCount,
  creationTime,
  updateTime,
  createdBy,
  artboard,
  artboardIDs,
}) => {
  const resolvedArtboard = artboard || {
    id: CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID,
    width,
    height,
  };
  return {
    schemaVersion: CREATIVE_TEMPLATE_SCHEMA_VERSION,
    id: templateID,
    advertiserID,
    name,
    status,
    thumbnailUrl,
    artboard: resolvedArtboard,
    artboardIDs:
      artboardIDs || [resolvedArtboard.id || CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID],
    nodeCount,
    ...buildTimeFields({ creationTime, updateTime }),
    createdBy,
  };
};

const assertCanWrite = ({ role }) => {
  if (!canWriteCreativeTemplates({ role })) {
    return { ok: false, reason: "unauthorized" };
  }
  return { ok: true };
};

export const startListenerCreativeTemplateCards =
  ({ onUpdatedDataCallback = () => {}, onReferenceAvailable = () => {} }) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());

      if (!advertiserID) {
        onUpdatedDataCallback({});
        return false;
      }

      const dbRef = ref(db, getCardsPath({ advertiserID }));
      onReferenceAvailable(dbRef);
      onValue(dbRef, (snapshot) => {
        onUpdatedDataCallback(snapshot.val() || {});
      });

      return true;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startListenerCreativeTemplateCards",
          tags: { action: "creativeTemplates" },
          level: "error",
        })
      );
      return false;
    }
  };

export const stopListenerCreativeTemplateCards =
  ({ listenerRef } = {}) =>
  async () => {
    if (!listenerRef) return;
    await off(listenerRef);
  };

export const startListenerCreativeTemplate =
  ({
    templateID,
    onUpdatedDataCallback = () => {},
    onReferenceAvailable = () => {},
  }) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());
      if (!advertiserID || !templateID) {
        onUpdatedDataCallback(null);
        return false;
      }

      const dbRef = ref(db, getTemplatePath({ advertiserID, templateID }));
      onReferenceAvailable(dbRef);
      onValue(dbRef, (snapshot) => {
        if (!snapshot.exists()) {
          onUpdatedDataCallback(null);
          return;
        }
        const value = snapshot.val() || {};
        const { doc, ...card } = value;
        onUpdatedDataCallback({ card, doc });
      });

      return true;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startListenerCreativeTemplate",
          context: { templateID },
          tags: { action: "creativeTemplates" },
          level: "error",
        })
      );
      return false;
    }
  };

export const stopListenerCreativeTemplate =
  ({ listenerRef } = {}) =>
  async () => {
    if (!listenerRef) return;
    await off(listenerRef);
  };

export const startGetCreativeTemplate =
  ({ templateID }) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());
      if (!advertiserID || !templateID) {
        return { ok: false, reason: "missingParameters" };
      }

      const snapshot = await get(
        ref(db, getTemplatePath({ advertiserID, templateID }))
      );

      if (!snapshot.exists()) {
        return { ok: false, reason: "notFound" };
      }

      const value = snapshot.val() || {};
      const { doc, ...card } = value;
      return { ok: true, data: { card, doc } };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startGetCreativeTemplate",
          context: { templateID },
          tags: { action: "creativeTemplates" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

export const startCreateEmptyCreativeTemplate =
  ({ name } = {}) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID, role } = dispatch(getPathDatabaseByRole());
      const writeCheck = assertCanWrite({ role });
      if (!writeCheck.ok) return writeCheck;
      if (!advertiserID) {
        return { ok: false, reason: "missingParameters" };
      }

      const templateID = dispatch(getKeyDatabase());
      const createdBy = dispatch(getUserID());
      const now = Date.now();
      const templateName = name || "Untitled";
      const width = CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_SIZE;
      const height = CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_SIZE;
      const doc = {
        schemaVersion: CREATIVE_TEMPLATE_SCHEMA_VERSION,
        id: templateID,
        advertiserID,
        name: templateName,
        status: CREATIVE_TEMPLATE_STATUS_DRAFT,
        editor: {
          activeArtboardID: CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID,
        },
        assets: {},
        variables: {},
        nodes: {},
        artboards: {
          [CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID]: {
            label: "source",
            width,
            height,
            nodeLayouts: {},
          },
        },
        audit: buildAudit({ createdBy, creationTime: now, updateTime: now }),
      };
      const card = buildCard({
        templateID,
        advertiserID,
        name: templateName,
        status: CREATIVE_TEMPLATE_STATUS_DRAFT,
        thumbnailUrl: "",
        width,
        height,
        nodeCount: 0,
        creationTime: now,
        updateTime: now,
        createdBy,
        ...getCardArtboardFields({
          artboards: doc.artboards,
          activeArtboardID: doc.editor.activeArtboardID,
        }),
      });

      const parsed = parseTemplateDoc({
        doc,
        dispatch,
        functionName: "startCreateEmptyCreativeTemplate",
        templateID,
      });
      if (!parsed.success) {
        return { ok: false, reason: "invalidGraph" };
      }

      const templatePath = getTemplatePath({ advertiserID, templateID });
      await update(ref(db), {
        [templatePath]: { ...card, doc: parsed.data },
        [`${getCardsPath({ advertiserID })}/${templateID}`]: card,
      });

      return { ok: true, data: { templateID } };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startCreateEmptyCreativeTemplate",
          tags: { action: "creativeTemplates" },
          level: "error",
        })
      );
      return { ok: false, reason: "createFailed" };
    }
  };

export const startCreateCreativeTemplateFromFile =
  ({ file, name }) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID, role } = dispatch(getPathDatabaseByRole());
      const writeCheck = assertCanWrite({ role });
      if (!writeCheck.ok) return writeCheck;
      if (!advertiserID || !file) {
        return { ok: false, reason: "missingParameters" };
      }

      const templateID = dispatch(getKeyDatabase());
      const assetID = dispatch(getKeyDatabase());
      const createdBy = dispatch(getUserID());
      const url = await dispatch(
        startUploadFileAws(
          file,
          file.type,
          getUploadPath({ advertiserID, templateID }),
          true,
          true
        )
      );

      if (!url) {
        return { ok: false, reason: "uploadFailed" };
      }

      const { width: imageWidth, height: imageHeight } =
        await getImageDimensionsFromFile({ file });
      const { width, height } = resolveArtboardSizeFromDimensions({
        width: imageWidth,
        height: imageHeight,
      });
      const now = Date.now();
      const templateName = name || file.name.replace(/\.[^.]+$/, "");
      const doc = {
        schemaVersion: CREATIVE_TEMPLATE_SCHEMA_VERSION,
        id: templateID,
        advertiserID,
        name: templateName,
        status: CREATIVE_TEMPLATE_STATUS_DRAFT,
        editor: {
          referenceImageAssetID: assetID,
          activeArtboardID: CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID,
        },
        assets: {
          reference: {
            type: "image",
            assetID,
            mimeType: file.type,
            url,
            width: imageWidth,
            height: imageHeight,
            role: "editorBackdrop",
          },
        },
        variables: {},
        nodes: {},
        artboards: {
          [CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID]: {
            label: "source",
            width,
            height,
            referenceAssetRef: "reference",
            nodeLayouts: {},
          },
        },
        audit: buildAudit({ createdBy, creationTime: now, updateTime: now }),
      };
      const card = buildCard({
        templateID,
        advertiserID,
        name: templateName,
        status: CREATIVE_TEMPLATE_STATUS_DRAFT,
        thumbnailUrl: url,
        width,
        height,
        nodeCount: 0,
        creationTime: now,
        updateTime: now,
        createdBy,
        ...getCardArtboardFields({
          artboards: doc.artboards,
          activeArtboardID: doc.editor.activeArtboardID,
        }),
      });

      const parsed = parseTemplateDoc({
        doc,
        dispatch,
        functionName: "startCreateCreativeTemplateFromFile",
        templateID,
      });
      if (!parsed.success) {
        return { ok: false, reason: "invalidGraph" };
      }

      const templatePath = getTemplatePath({ advertiserID, templateID });
      await update(ref(db), {
        [templatePath]: { ...card, doc: parsed.data },
        [`${getCardsPath({ advertiserID })}/${templateID}`]: card,
        [getAssetPath({ advertiserID, assetID })]: {
          id: assetID,
          advertiserID,
          type: "image",
          url,
          mimeType: file.type,
          width,
          height,
          role: "editorBackdrop",
          creationTime: now,
          creationTimeNegative: now * -1,
        },
      });

      return { ok: true, data: { templateID } };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startCreateCreativeTemplateFromFile",
          tags: { action: "creativeTemplates" },
          level: "error",
        })
      );
      return { ok: false, reason: "uploadFailed" };
    }
  };

const patchTemplateAnalysis = async ({
  advertiserID,
  templateID,
  analysis,
}) => {
  await update(ref(db), {
    [`${getTemplateDocPath({ advertiserID, templateID })}/editor/analysis`]:
      analysis,
  });
};

export const startAttachOriginCreativeAndAnalyze =
  ({ templateID, file } = {}) =>
  async (dispatch) => {
    const alertID = getUniqueID();
    let notificationId = alertID;
    const payloadSnapshot = { templateID };

    try {
      const { userID: advertiserID, role } = dispatch(getPathDatabaseByRole());
      const writeCheck = assertCanWrite({ role });
      if (!writeCheck.ok) return writeCheck;
      if (!advertiserID || !templateID) {
        return { ok: false, reason: "missingParameters" };
      }

      const current = await dispatch(startGetCreativeTemplate({ templateID }));
      if (!current.ok) return current;

      const existingDoc = current.data.doc || {};
      const existingReference = existingDoc.assets?.reference || {};
      let url = existingReference.url;
      let width =
        existingReference.width ||
        existingDoc.artboards?.[CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID]?.width;
      let height =
        existingReference.height ||
        existingDoc.artboards?.[CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID]?.height;
      let mimeType = existingReference.mimeType;
      let assetID =
        existingDoc.editor?.referenceImageAssetID || existingReference.assetID;

      if (file) {
        assetID = assetID || dispatch(getKeyDatabase());
        url = await dispatch(
          startUploadFileAws(
            file,
            file.type,
            getUploadPath({ advertiserID, templateID }),
            true,
            true
          )
        );
        if (!url) {
          return { ok: false, reason: "uploadFailed" };
        }
        const dimensions = await getImageDimensionsFromFile({ file });
        width = dimensions.width;
        height = dimensions.height;
        mimeType = file.type;
      }

      if (!url || !assetID || !width || !height) {
        return { ok: false, reason: "missingParameters" };
      }

      const { width: artboardWidth, height: artboardHeight } =
        resolveArtboardSizeFromDimensions({ width, height });
      const now = Date.now();
      const referenceArtboardID = findReferenceArtboardID({
        artboards: existingDoc.artboards,
        assets: existingDoc.assets,
        reference: { width, height, url, assetID },
      });
      const existingArtboards = existingDoc.artboards || {
        [CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID]: {
          label: "source",
          width: artboardWidth,
          height: artboardHeight,
          nodeLayouts: {},
        },
      };
      const targetArtboardID =
        existingArtboards[referenceArtboardID]
          ? referenceArtboardID
          : CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID;
      const targetArtboard = existingArtboards[targetArtboardID] || {};
      const doc = {
        ...existingDoc,
        editor: {
          ...(existingDoc.editor || {}),
          activeArtboardID:
            existingDoc.editor?.activeArtboardID ||
            CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID,
          referenceImageAssetID: assetID,
          analysis: {
            status: CREATIVE_TEMPLATE_ANALYSIS_STATUS.PROCESSING,
          },
        },
        assets: {
          ...(existingDoc.assets || {}),
          reference: {
            type: "image",
            assetID,
            mimeType,
            url,
            width,
            height,
            role: "editorBackdrop",
          },
        },
        artboards: {
          ...existingArtboards,
          [targetArtboardID]: {
            ...targetArtboard,
            width: artboardWidth,
            height: artboardHeight,
            referenceAssetRef: "reference",
            nodeLayouts: targetArtboard.nodeLayouts || {},
          },
        },
        audit: buildAudit({ existingAudit: existingDoc.audit, updateTime: now }),
      };

      const parsed = parseTemplateDoc({
        doc,
        dispatch,
        functionName: "startAttachOriginCreativeAndAnalyze",
        templateID,
      });
      if (!parsed.success) {
        return { ok: false, reason: "invalidGraph" };
      }

      const existingCard = current.data.card || {};
      const card = buildCard({
        templateID,
        advertiserID,
        name: existingCard.name,
        status: existingCard.status,
        thumbnailUrl: url,
        width,
        height,
        nodeCount: existingCard.nodeCount || 0,
        creationTime: resolveCreationTime({ card: existingCard, fallback: now }),
        updateTime: now,
        createdBy: existingCard.createdBy,
        ...getCardArtboardFields({
          artboards: parsed.data.artboards,
          activeArtboardID: parsed.data.editor?.activeArtboardID,
        }),
      });

      const templatePath = getTemplatePath({ advertiserID, templateID });
      await update(ref(db), {
        [templatePath]: { ...card, doc: parsed.data },
        [`${getCardsPath({ advertiserID })}/${templateID}`]: card,
        [getAssetPath({ advertiserID, assetID })]: {
          id: assetID,
          advertiserID,
          type: "image",
          url,
          mimeType,
          width,
          height,
          role: "editorBackdrop",
          creationTime: now,
          creationTimeNegative: now * -1,
        },
      });

      dispatch(
        addNotification({
          type: "analyzingCreativeTemplate",
          id: alertID,
          status: ALERTS_ASYNC_STATUS.LOADING,
          progress: 0,
          jobType: CREATIVE_JOB_TYPES.analyzeCreativeTemplate,
          messageKey: getCreativeJobAlertMessageKey({
            jobType: CREATIVE_JOB_TYPES.analyzeCreativeTemplate,
          }),
          generationData: {
            ...payloadSnapshot,
            imageUrl: url,
            isGenerating: true,
          },
        })
      );

      const data = await createCreativeJobAndWaitForResponse({
        dispatch,
        jobType: CREATIVE_JOB_TYPES.analyzeCreativeTemplate,
        payload: {
          templateID,
          imageUrl: url,
        },
        onJobCreated: ({ jobID }) => {
          dispatch(
            promoteCreativeJobNotification({ fromId: notificationId, jobID })
          );
          notificationId = jobID;
          update(ref(db), {
            [`${getTemplateDocPath({ advertiserID, templateID })}/editor/analysis/jobID`]:
              jobID,
          }).catch(() => {});
        },
      });

      const latest = await dispatch(startGetCreativeTemplate({ templateID }));
      const analyzedNodes = latest.ok ? latest.data?.doc?.nodes : null;
      if (analyzedNodes) {
        await update(ref(db), {
          [`${getTemplateDocPath({ advertiserID, templateID })}/artboards/${targetArtboardID}/nodes`]:
            analyzedNodes,
        });
      }

      dispatch(
        updateNotification(notificationId, {
          status: ALERTS_ASYNC_STATUS.SUCCESS,
          progress: 100,
          generationData: mapJobResultToGenerationData({
            job: {
              jobType: CREATIVE_JOB_TYPES.analyzeCreativeTemplate,
              status: CREATIVE_JOB_STATUS.SUCCESS,
              response: data,
            },
            payloadSnapshot,
          }),
        })
      );

      return { ok: true, data: { templateID } };
    } catch (error) {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());
      if (advertiserID && templateID) {
        await patchTemplateAnalysis({
          advertiserID,
          templateID,
          analysis: {
            status: CREATIVE_TEMPLATE_ANALYSIS_STATUS.ERROR,
            errorCode: error?.code || "ERROR_OCCURRED",
          },
        }).catch(() => {});
      }

      const errorFormatted = dispatch(
        LaunchError(error, true, [], {
          functionName: "startAttachOriginCreativeAndAnalyze",
          context: { templateID },
          creativeJobType: CREATIVE_JOB_TYPES.analyzeCreativeTemplate,
          tags: { action: "creativeTemplates" },
          level: "error",
        })
      );

      dispatch(
        updateNotification(notificationId, {
          status: ALERTS_ASYNC_STATUS.ERROR,
          message: errorFormatted?.message || error?.message,
          progress: 100,
          generationData: {
            ...payloadSnapshot,
            isGenerating: false,
          },
        })
      );

      return { ok: false, reason: "analyzeFailed" };
    }
  };

export const startUploadCreativeTemplateLayerAsset =
  ({ templateID, file }) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID, role } = dispatch(getPathDatabaseByRole());
      const writeCheck = assertCanWrite({ role });
      if (!writeCheck.ok) return writeCheck;
      if (!advertiserID || !templateID || !file) {
        return { ok: false, reason: "missingParameters" };
      }

      await getImageDimensionsFromFile({ file });
      const url = await dispatch(
        startUploadFileAws(
          file,
          file.type,
          getUploadPath({ advertiserID, templateID }),
          true,
          true
        )
      );
      if (!url) {
        return { ok: false, reason: "uploadFailed" };
      }

      const assetID = dispatch(getKeyDatabase());

      return {
        ok: true,
        data: {
          assetID,
          url,
          mimeType: file.type || "image/png",
        },
      };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startUploadCreativeTemplateLayerAsset",
          context: { templateID },
          tags: { action: "creativeTemplates" },
          level: "error",
        })
      );
      return { ok: false, reason: "uploadFailed" };
    }
  };

export const startAttachArtboardReference =
  ({ templateID, artboardID, file, artboard, artboards } = {}) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID, role } = dispatch(getPathDatabaseByRole());
      const writeCheck = assertCanWrite({ role });
      if (!writeCheck.ok) return writeCheck;
      if (!advertiserID || !templateID || !artboardID || !file) {
        return { ok: false, reason: "missingParameters" };
      }

      const current = await dispatch(startGetCreativeTemplate({ templateID }));
      if (!current.ok) return current;

      const existingDoc = current.data.doc || {};
      const existingArtboards = existingDoc.artboards || {};
      const sourceArtboard =
        artboard ||
        (artboards || {})[artboardID] ||
        existingArtboards[artboardID];
      if (!sourceArtboard) {
        return { ok: false, reason: "invalidGraph" };
      }

      const assetKey = getReferenceAssetKey({ artboardID });
      const existingAsset =
        existingDoc.assets?.[assetKey] ||
        (sourceArtboard.referenceAssetRef
          ? existingDoc.assets?.[sourceArtboard.referenceAssetRef]
          : null) ||
        {};
      const assetID = existingAsset.assetID || dispatch(getKeyDatabase());
      const url = await dispatch(
        startUploadFileAws(
          file,
          file.type,
          getUploadPath({ advertiserID, templateID }),
          true,
          true
        )
      );
      if (!url) {
        return { ok: false, reason: "uploadFailed" };
      }

      const { width, height } = await getImageDimensionsFromFile({ file });
      const now = Date.now();
      const nextAsset = {
        type: "image",
        assetID,
        mimeType: file.type || "image/png",
        url,
        width,
        height,
        role: "editorBackdrop",
      };
      const nextAssets = {
        ...(existingDoc.assets || {}),
        [assetKey]: nextAsset,
      };
      const hydratedArtboards = hydrateArtboards({
        artboards: {
          ...existingArtboards,
          ...(artboards || {}),
          [artboardID]: {
            ...(existingArtboards[artboardID] || {}),
            ...sourceArtboard,
            referenceAssetRef: assetKey,
          },
        },
        nodes: existingDoc.nodes || {},
      });
      const graph = sanitizeArtboardsForPersist({
        artboards: hydratedArtboards,
      });
      const nextArtboard = {
        ...graph.artboards[artboardID],
        referenceAssetRef: assetKey,
        nodes: sanitizeNodesForPersist({
          nodes: graph.artboards[artboardID]?.nodes || {},
          isRoot: true,
        }),
      };
      const nextArtboards = {
        ...graph.artboards,
        [artboardID]: nextArtboard,
      };
      const nextEditor = {
        ...(existingDoc.editor || {}),
        activeArtboardID: resolveActiveArtboardID({
          artboards: nextArtboards,
          activeArtboardID: artboardID,
        }),
      };
      if (assetKey === "reference") {
        nextEditor.referenceImageAssetID = assetID;
      }

      const parsed = parseTemplateDoc({
        doc: {
          ...existingDoc,
          editor: nextEditor,
          assets: nextAssets,
          artboards: nextArtboards,
          audit: buildAudit({
            existingAudit: existingDoc.audit,
            updateTime: now,
          }),
        },
        dispatch,
        functionName: "startAttachArtboardReference",
        templateID,
      });
      if (!parsed.success) {
        return { ok: false, reason: "invalidGraph" };
      }

      const docPath = getTemplateDocPath({ advertiserID, templateID });
      const templatePath = getTemplatePath({ advertiserID, templateID });
      const persistArtboard = parsed.data.artboards[artboardID];
      const updates = {
        [`${docPath}/assets/${assetKey}`]: nextAsset,
        [`${docPath}/audit`]: parsed.data.audit,
        [getAssetPath({ advertiserID, assetID })]: {
          id: assetID,
          advertiserID,
          type: "image",
          url,
          mimeType: file.type || "image/png",
          width,
          height,
          role: "editorBackdrop",
          creationTime: now,
          creationTimeNegative: now * -1,
        },
      };

      if (existingArtboards[artboardID]) {
        updates[`${docPath}/artboards/${artboardID}/referenceAssetRef`] =
          assetKey;
      } else {
        updates[`${docPath}/artboards/${artboardID}`] = persistArtboard;
      }

      if (assetKey === "reference") {
        updates[`${docPath}/editor/referenceImageAssetID`] = assetID;
        updates[`${templatePath}/thumbnailUrl`] = url;
        updates[`${getCardsPath({ advertiserID })}/${templateID}/thumbnailUrl`] =
          url;
      }

      await update(ref(db), updates);

      return {
        ok: true,
        data: {
          templateID,
          artboardID,
          assetKey,
          asset: nextAsset,
        },
      };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startAttachArtboardReference",
          context: { templateID, artboardID },
          tags: { action: "creativeTemplates" },
          level: "error",
        })
      );
      return { ok: false, reason: "uploadFailed" };
    }
  };

export const startSaveCreativeTemplate =
  ({ templateID, name, nodes, variables, assets, artboards, activeArtboardID }) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID, role } = dispatch(getPathDatabaseByRole());
      const writeCheck = assertCanWrite({ role });
      if (!writeCheck.ok) return writeCheck;

      const current = await dispatch(startGetCreativeTemplate({ templateID }));
      if (!current.ok) return current;

      if (
        current.data.doc?.editor?.analysis?.status ===
        CREATIVE_TEMPLATE_ANALYSIS_STATUS.PROCESSING
      ) {
        return { ok: false, reason: "analyzing" };
      }

      const existingDoc = current.data.doc || {};
      const now = Date.now();
      const nextName = name || existingDoc.name;
      const hydratedArtboards = hydrateArtboards({
        artboards: artboards || existingDoc.artboards || {},
        nodes: nodes || existingDoc.nodes || {},
      });
      const resolvedActiveID = resolveActiveArtboardID({
        artboards: hydratedArtboards,
        activeArtboardID:
          activeArtboardID || existingDoc.editor?.activeArtboardID,
      });
      const artboardsWithActive = nodes
        ? {
            ...hydratedArtboards,
            [resolvedActiveID]: {
              ...(hydratedArtboards[resolvedActiveID] || {}),
              nodes,
            },
          }
        : hydratedArtboards;
      const exceedsCap = Object.values(artboardsWithActive).some(
        (artboard) =>
          countNodes({ nodes: artboard?.nodes || {} }) >
          CREATIVE_TEMPLATE_MAX_NODES
      );
      if (exceedsCap) {
        return { ok: false, reason: "invalidGraph" };
      }

      const graph = sanitizeArtboardsForPersist({
        artboards: artboardsWithActive,
      });
      const nextActiveArtboardID = graph.artboards[resolvedActiveID]
        ? resolvedActiveID
        : Object.keys(graph.artboards)[0] ||
          CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID;
      const mirroredNodes =
        graph.artboards[nextActiveArtboardID]?.nodes ||
        graph.artboards[CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID]?.nodes ||
        {};
      const doc = {
        ...existingDoc,
        name: nextName,
        status: CREATIVE_TEMPLATE_STATUS_DRAFT,
        nodes: mirroredNodes,
        variables: variables || {},
        assets: assets || existingDoc.assets,
        artboards: graph.artboards,
        editor: {
          ...(existingDoc.editor || {}),
          activeArtboardID: nextActiveArtboardID,
        },
        audit: buildAudit({ existingAudit: existingDoc.audit, updateTime: now }),
      };

      const parsed = parseTemplateDoc({
        doc,
        dispatch,
        functionName: "startSaveCreativeTemplate",
        templateID,
      });
      if (!parsed.success) {
        return { ok: false, reason: "invalidGraph" };
      }

      const existingCard = current.data.card || {};
      const cardFields = getCardArtboardFields({
        artboards: parsed.data.artboards,
        activeArtboardID: parsed.data.editor?.activeArtboardID,
      });
      const card = buildCard({
        templateID,
        advertiserID,
        name: nextName,
        status: CREATIVE_TEMPLATE_STATUS_DRAFT,
        thumbnailUrl:
          parsed.data.assets?.reference?.url ||
          existingCard.thumbnailUrl ||
          "",
        width: cardFields.artboard.width,
        height: cardFields.artboard.height,
        nodeCount: countAllArtboardNodes({
          artboards: parsed.data.artboards,
          nodes: parsed.data.nodes,
        }),
        creationTime: resolveCreationTime({ card: existingCard, fallback: now }),
        updateTime: now,
        createdBy: existingCard.createdBy,
        ...cardFields,
      });

      const templatePath = getTemplatePath({ advertiserID, templateID });
      await update(ref(db), {
        [templatePath]: { ...card, doc: parsed.data },
        [`${getCardsPath({ advertiserID })}/${templateID}`]: card,
      });

      return { ok: true, data: { templateID } };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startSaveCreativeTemplate",
          context: { templateID },
          tags: { action: "creativeTemplates" },
          level: "error",
        })
      );
      return { ok: false, reason: "saveFailed" };
    }
  };

export const startDuplicateCreativeTemplate =
  ({ templateID }) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID, role } = dispatch(getPathDatabaseByRole());
      const writeCheck = assertCanWrite({ role });
      if (!writeCheck.ok) return writeCheck;

      const current = await dispatch(startGetCreativeTemplate({ templateID }));
      if (!current.ok) return current;

      const newTemplateID = dispatch(getKeyDatabase());
      const createdBy = dispatch(getUserID());
      const now = Date.now();
      const sourceDoc = current.data.doc || {};
      const sourceCard = current.data.card || {};
      const backdropEntries = listBackdropAssets({
        assets: sourceDoc.assets,
        artboards: sourceDoc.artboards,
      });
      const nextAssets = { ...(sourceDoc.assets || {}) };
      const backdropWrites = backdropEntries.map(({ key, asset }) => {
        const newAssetID = dispatch(getKeyDatabase());
        nextAssets[key] = {
          ...asset,
          assetID: newAssetID,
          role: "editorBackdrop",
        };
        return { assetID: newAssetID, asset: nextAssets[key] };
      });
      const hasReference = Boolean(nextAssets.reference?.url);

      const nextEditor = {
        ...(sourceDoc.editor || {}),
        activeArtboardID:
          sourceDoc.editor?.activeArtboardID ||
          CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID,
      };
      delete nextEditor.analysis;
      if (hasReference && nextAssets.reference?.assetID) {
        nextEditor.referenceImageAssetID = nextAssets.reference.assetID;
      } else {
        delete nextEditor.referenceImageAssetID;
      }

      const doc = {
        ...sourceDoc,
        id: newTemplateID,
        advertiserID,
        name: `${sourceDoc.name} copy`,
        status: CREATIVE_TEMPLATE_STATUS_DRAFT,
        editor: nextEditor,
        assets: nextAssets,
        audit: buildAudit({ createdBy, creationTime: now, updateTime: now }),
      };

      const parsed = parseTemplateDoc({
        doc,
        dispatch,
        functionName: "startDuplicateCreativeTemplate",
        templateID: newTemplateID,
      });
      if (!parsed.success) {
        return { ok: false, reason: "invalidGraph" };
      }

      const card = buildCard({
        templateID: newTemplateID,
        advertiserID,
        name: doc.name,
        status: CREATIVE_TEMPLATE_STATUS_DRAFT,
        thumbnailUrl: sourceCard.thumbnailUrl || "",
        width:
          sourceCard.artboard?.width ||
          nextAssets.reference?.width ||
          CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_SIZE,
        height:
          sourceCard.artboard?.height ||
          nextAssets.reference?.height ||
          CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_SIZE,
        nodeCount: countAllArtboardNodes({
          artboards: doc.artboards,
          nodes: doc.nodes,
        }),
        creationTime: now,
        updateTime: now,
        createdBy,
        ...getCardArtboardFields({
          artboards: parsed.data.artboards,
          activeArtboardID: parsed.data.editor?.activeArtboardID,
        }),
      });

      const templatePath = getTemplatePath({
        advertiserID,
        templateID: newTemplateID,
      });
      const updates = {
        [templatePath]: { ...card, doc: parsed.data },
        [`${getCardsPath({ advertiserID })}/${newTemplateID}`]: card,
      };
      if (backdropWrites.length) {
        backdropWrites.forEach(({ assetID, asset }) => {
          updates[getAssetPath({ advertiserID, assetID })] = {
            id: assetID,
            advertiserID,
            type: "image",
            url: asset.url,
            mimeType: asset.mimeType,
            width: asset.width,
            height: asset.height,
            role: "editorBackdrop",
            creationTime: now,
            creationTimeNegative: now * -1,
          };
        });
      }
      await update(ref(db), updates);

      return { ok: true, data: { templateID: newTemplateID } };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startDuplicateCreativeTemplate",
          context: { templateID },
          tags: { action: "creativeTemplates" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

export const startDeleteCreativeTemplate =
  ({ templateID }) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID, role } = dispatch(getPathDatabaseByRole());
      const writeCheck = assertCanWrite({ role });
      if (!writeCheck.ok) return writeCheck;

      const current = await dispatch(startGetCreativeTemplate({ templateID }));
      if (!current.ok) return current;

      const backdropEntries = listBackdropAssets({
        assets: current.data.doc?.assets,
        artboards: current.data.doc?.artboards,
      });
      const assetIDs = new Set(
        backdropEntries
          .map((entry) => entry.asset?.assetID)
          .filter(Boolean)
      );
      const legacyAssetID = current.data.doc?.editor?.referenceImageAssetID;
      if (legacyAssetID) assetIDs.add(legacyAssetID);
      const updates = {
        [getTemplatePath({ advertiserID, templateID })]: null,
        [`${getCardsPath({ advertiserID })}/${templateID}`]: null,
      };
      assetIDs.forEach((assetID) => {
        updates[getAssetPath({ advertiserID, assetID })] = null;
      });

      await update(ref(db), updates);
      return { ok: true };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startDeleteCreativeTemplate",
          context: { templateID },
          tags: { action: "creativeTemplates" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

const PRODUCE_BATCH_SIZE = 5;
const PRODUCE_MINUTES_PER_BATCH = 3;
const PRODUCE_MAX_ROWS = 50;
const MINUTE_IN_MS = 60 * 1000;

const serializeProduceRows = ({ rows = [], columns = [] }) =>
  rows.map((row) => {
    const next = {};
    columns.forEach((column) => {
      next[column.key] = row?.[column.key] ?? "";
    });
    return next;
  });

export const startProduceCreativeTemplate =
  ({ templateID, produceID, language, rows, columns, artboardIDs = [] }) =>
  async (dispatch) => {
    const alertID = getUniqueID();
    let notificationId = alertID;
    const serializedRows = serializeProduceRows({ rows, columns });
    const itemCount = serializedRows.length;
    const selectedArtboardIDs = Array.isArray(artboardIDs)
      ? artboardIDs.filter(Boolean)
      : [];
    const artboardCount = Math.max(selectedArtboardIDs.length, 1);
    const assetCount = itemCount * artboardCount;
    const payloadSnapshot = {
      templateID,
      produceID,
      language,
      itemCount: assetCount,
      artboardCount,
      mediaType: "image",
    };

    try {
      if (!templateID || !produceID || itemCount === 0) {
        return { ok: false, reason: "missingParameters" };
      }
      if (itemCount > PRODUCE_MAX_ROWS) {
        return { ok: false, reason: "tooManyRows" };
      }

      await dispatch(
        startSaveCreativeTemplateProduce({
          produceID,
          templateID,
          language,
          rows: serializedRows,
          itemCount,
          artboardIDs: selectedArtboardIDs,
          artboardCount,
          status: CREATIVE_TEMPLATE_PRODUCE_STATUS.PROCESSING,
        })
      );

      dispatch(
        addNotification({
          type: "producingCreativeTemplate",
          id: alertID,
          status: ALERTS_ASYNC_STATUS.LOADING,
          progress: 0,
          jobType: CREATIVE_JOB_TYPES.produceCreativeTemplate,
          messageKey: getCreativeJobAlertMessageKey({
            jobType: CREATIVE_JOB_TYPES.produceCreativeTemplate,
          }),
          generationData: {
            ...payloadSnapshot,
            isGenerating: true,
          },
        })
      );

      const timeoutMs =
        Math.ceil(assetCount / PRODUCE_BATCH_SIZE) *
          PRODUCE_MINUTES_PER_BATCH *
          MINUTE_IN_MS +
        MINUTE_IN_MS;

      const data = await createCreativeJobAndWaitForResponse({
        dispatch,
        jobType: CREATIVE_JOB_TYPES.produceCreativeTemplate,
        payload: {
          templateID,
          produceID,
          language,
          itemCount,
          artboardIDs: selectedArtboardIDs,
          artboardCount,
          rows: serializedRows,
        },
        timeoutMs,
        onJobCreated: ({ jobID }) => {
          dispatch(
            promoteCreativeJobNotification({ fromId: notificationId, jobID })
          );
          notificationId = jobID;
        },
      });

      dispatch(
        updateNotification(notificationId, {
          status: ALERTS_ASYNC_STATUS.SUCCESS,
          progress: 100,
          generationData: mapJobResultToGenerationData({
            job: {
              jobType: CREATIVE_JOB_TYPES.produceCreativeTemplate,
              status: CREATIVE_JOB_STATUS.SUCCESS,
              response: data,
            },
            payloadSnapshot,
          }),
        })
      );

      return { ok: true, data };
    } catch (error) {
      const errorFormatted = dispatch(
        LaunchError(error, true, [], {
          functionName: "startProduceCreativeTemplate",
          context: { templateID, produceID, itemCount, artboardCount },
          creativeJobType: CREATIVE_JOB_TYPES.produceCreativeTemplate,
          tags: { action: "creativeTemplates" },
          level: "error",
        })
      );

      dispatch(
        updateNotification(notificationId, {
          status: ALERTS_ASYNC_STATUS.ERROR,
          message: errorFormatted?.message || error?.message,
          progress: 100,
          generationData: {
            ...payloadSnapshot,
            isGenerating: false,
          },
        })
      );

      await dispatch(
        startUpdateCreativeTemplateProduceStatus({
          produceID,
          status: CREATIVE_TEMPLATE_PRODUCE_STATUS.ERROR,
          error: errorFormatted?.message || error?.message,
        })
      );

      return { ok: false, reason: "produceFailed" };
    }
  };

export { getTemplateDocPath };
